import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart3, Package, Receipt, Smartphone, Truck, Warehouse } from "lucide-react";
import api from "@/lib/api";
import { formatIDR, formatDateTime, REF_TYPE_LABELS } from "@/lib/format";

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.06, duration: 0.3 },
});

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="grid h-64 place-items-center" data-testid="dashboard-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600" />
      </div>
    );
  }

  const cards = [
    { label: "Total Products", value: data.total_products, icon: Smartphone, testid: "kpi-total-produk" },
    { label: "Total Stock Units", value: data.total_units, icon: Package, testid: "kpi-total-unit" },
    { label: "Stock Value (FIFO)", value: formatIDR(data.stock_value), icon: BarChart3, testid: "kpi-nilai-stok" },
    { label: "Warehouses", value: data.total_warehouses, icon: Warehouse, testid: "kpi-gudang" },
    { label: "Transfers In Transit", value: data.transfers_dikirim, icon: Truck, testid: "kpi-transfer" },
    { label: "Sales This Month", value: `${data.sales_month_count} invoices`, sub: formatIDR(data.sales_month_total), icon: Receipt, testid: "kpi-penjualan" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Dashboard</h1>
        <p className="mt-1 text-sm text-zinc-500">Overview of stock and activity across your warehouses.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((c, i) => (
          <motion.div key={c.label} {...cardAnim(i)} className="rounded-md border border-zinc-200 bg-white p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-zinc-500">{c.label}</p>
              <c.icon className="h-4 w-4 text-blue-600" />
            </div>
            <p className="tnum mt-3 text-2xl font-bold tracking-tight text-zinc-900" data-testid={c.testid}>
              {c.value}
            </p>
            {c.sub && <p className="tnum mt-1 text-sm font-medium text-emerald-600">{c.sub}</p>}
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <motion.div {...cardAnim(6)} className="rounded-md border border-zinc-200 bg-white p-6">
          <h2 className="text-lg font-bold tracking-tight text-zinc-900">Low Stock</h2>
          <p className="mt-1 text-sm text-zinc-500">Products with 5 units or fewer left per warehouse.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="low-stock-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-400">
                  <th className="py-2 pr-4 font-medium">Product</th>
                  <th className="py-2 pr-4 font-medium">Warehouse</th>
                  <th className="py-2 text-right font-medium">Left</th>
                </tr>
              </thead>
              <tbody>
                {data.low_stock.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-zinc-400" data-testid="low-stock-empty">
                      No low stock items
                    </td>
                  </tr>
                )}
                {data.low_stock.map((r, i) => (
                  <tr key={i} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="py-2.5 pr-4 font-medium text-zinc-800">{r.product_name}</td>
                    <td className="py-2.5 pr-4 text-zinc-500">{r.warehouse_name}</td>
                    <td className={`tnum py-2.5 text-right font-semibold ${r.qty <= 2 ? "text-red-600" : "text-amber-600"}`}>{r.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div {...cardAnim(7)} className="rounded-md border border-zinc-200 bg-white p-6">
          <h2 className="text-lg font-bold tracking-tight text-zinc-900">Recent Activity</h2>
          <p className="mt-1 text-sm text-zinc-500">Latest stock movements across all warehouses.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="recent-movements-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wide text-zinc-400">
                  <th className="py-2 pr-4 font-medium">Time</th>
                  <th className="py-2 pr-4 font-medium">Product</th>
                  <th className="py-2 pr-4 font-medium">Warehouse</th>
                  <th className="py-2 pr-4 font-medium">Ref</th>
                  <th className="py-2 text-right font-medium">Qty</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_movements.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-zinc-400" data-testid="recent-movements-empty">
                      No activity yet
                    </td>
                  </tr>
                )}
                {data.recent_movements.map((m) => (
                  <tr key={m.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50">
                    <td className="py-2.5 pr-4 text-zinc-500">{formatDateTime(m.created_at)}</td>
                    <td className="py-2.5 pr-4 font-medium text-zinc-800">{m.product_name}</td>
                    <td className="py-2.5 pr-4 text-zinc-500">{m.warehouse_name}</td>
                    <td className="py-2.5 pr-4 text-zinc-500">
                      <span className="font-mono text-xs">{m.ref_no}</span>
                      <span className="ml-1 text-xs text-zinc-400">({REF_TYPE_LABELS[m.ref_type] || m.ref_type})</span>
                    </td>
                    <td className={`tnum py-2.5 text-right font-semibold ${m.direction === "in" ? "text-emerald-600" : "text-red-600"}`}>
                      {m.direction === "in" ? "+" : "-"}{m.qty}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
